import { Trigger } from "@/components/forms/Form/types";
import { convertHjalpmedelData, Hjalpmedel } from "@/components/forms/reusables/hjalpmedelHelper";
import { convertImplantatData, Implantat } from "@/components/forms/reusables/implantatHelper";
import { convertPrioritetData } from "@/components/forms/reusables/prioritetHelper";
import { convertResursData, Resurs } from "@/components/forms/reusables/resurserHelper";
import { useStore } from "@/store";
import { convertDiagnoskodData, Diagnoskod } from "@/store/modules/diagnoskodModule";
import { convertAnestesiData } from "@/store/modules/operation/anestesiModule";
import { convertAntibiotikaprofylaxData } from "@/store/modules/operation/antibiotikaprofylaxModule";
import { convertBlockadData } from "@/store/modules/operation/blockadModule";
import { convertOperationskodData, Operationskod } from "@/store/modules/operation/operationskodModule";
import { convertPatientlageData } from "@/store/modules/operation/patientlageModule";
import { convertTrombosprofylaxData } from "@/store/modules/operation/trombosprofylaxModule";
import {
    convertStudieKvalitetsregisterData,
    StudieKvalitetsregister,
} from "@/components/forms/reusables/studieKvalitetsregisterHelper";

const ogiltigKodSuffix = "Värdet är inte giltigt just nu";

const fieldSuffixes: { [key: string]: (arg: any) => string | null } = {
    huvuddiagnos: ({ typoperation }: { typoperation: any }) =>
        typoperation?.huvuddiagnos_display.is_valid_now ? null : ogiltigKodSuffix,
    bidiagnoser: (diagnos_display: any) => (diagnos_display.is_valid_now ? null : ogiltigKodSuffix),
    huvudoperationskod: ({ typoperation }: { typoperation: any }) =>
        typoperation?.huvudoperationskod_display.is_valid_now ? null : ogiltigKodSuffix,
    bioperationskoder: ({ typoperation }: { typoperation: any }) =>
        typoperation?.bioperationskoder_display ? null : ogiltigKodSuffix,
    lateralitet: () => null,
    anestesi: () => null,
    optid: () => null,
    forberedelser: () => null,
    avveckling: () => null,
    antal_dagar_pa_avdelning: () => null,
    planerad_vardform: () => null,
    prio: () => null,
    oplage: () => null,
    blockad: () => null,
    implantat: () => null,
    resurser: () => null,
    hjalpmedel: () => null,
    antibiotikaprofylax: () => null,
    trombosprofylax: () => null,
    operationsinformation: () => null,
    studie_kvalitetsregister: () => null,
};

const dependentFields = [
    "huvuddiagnos",
    "bidiagnoser",
    "huvudoperationskod",
    "bioperationskoder",
    "lateralitet",
    "anestesi",
    "optid",
    "forberedelser",
    "avveckling",
    "antal_dagar_pa_avdelning",
    "planerad_vardform",
    "prio",
    "oplage",
    "blockad",
    "implantat",
    "resurser",
    "hjalpmedel",
    "antibiotikaprofylax",
    "trombosprofylax",
    "operationsinformation",
    "studie_kvalitetsregister",
];

// converts the value from the typoperation to the expected format for the field
export const fieldsConverters: { [key: string]: any } = {
    huvuddiagnos: ({ value }: { value: any }) => {
        return convertDiagnoskodData({
            diagnoskod: value?.huvuddiagnos_display,
        });
    },
    bidiagnoser: ({ value }: { value: any }) => {
        return value?.bidiagnoser_display.map((item: Diagnoskod) => {
            return {
                id: convertDiagnoskodData({ diagnoskod: item }),
                suffix: fieldSuffixes.bidiagnoser(item),
            };
        });
    },
    huvudoperationskod: ({ value }: { value: any }) => {
        return convertOperationskodData({
            operationskod: value?.huvudoperationskod_display,
        });
    },
    bioperationskoder: ({ value }: { value: any }) => {
        return value?.bioperationskoder_display?.map((item: Operationskod) => {
            return {
                id: convertOperationskodData({ operationskod: item }),
                suffix: fieldSuffixes.bioperationskoder({ typoperation: value }),
            };
        });
    },
    lateralitet: ({ value }: { value: any }) => {
        return value.lateralitet;
    },
    anestesi: ({ value }: { value: any }) => {
        return convertAnestesiData({ anestesi: value?.anestesi_display });
    },
    optid: ({ value }: { value: any }) => {
        return value.optid;
    },
    forberedelser: ({ value }: { value: any }) => {
        return value.forberedelser || 45;
    },
    avveckling: ({ value }: { value: any }) => {
        return value.avveckling || 15;
    },
    antal_dagar_pa_avdelning: ({ value }: { value: any }) => {
        return value.antal_dagar_pa_avdelning;
    },
    planerad_vardform: ({ value }: { value: any }) => {
        return value.planerad_vardform;
    },
    prio: ({ value }: { value: any }) => {
        return convertPrioritetData({ prioritet: value.prio_display });
    },
    oplage: ({ value }: { value: any }) => {
        return convertPatientlageData({ patientlage: value?.oplage_display });
    },
    blockad: ({ value }: { value: any }) => {
        return convertBlockadData({ blockad: value?.blockad_display });
    },
    implantat: ({ value }: { value: any }) => {
        return value?.implantat_display.map((item: Implantat) => {
            return { id: convertImplantatData({ implantat: item }) };
        });
    },
    resurser: ({ value }: { value: any }) => {
        return value?.resurser_display.map((item: Resurs) => {
            return { id: convertResursData({ resurs: item }) };
        });
    },
    hjalpmedel: ({ value }: { value: any }) => {
        return value?.hjalpmedel_display.map((item: Hjalpmedel) => {
            return { id: convertHjalpmedelData({ hjalpmedel: item }) };
        });
    },
    antibiotikaprofylax: ({ value }: { value: any }) => {
        return convertAntibiotikaprofylaxData({ antibiotikaprofylax: value?.antibiotikaprofylax_display });
    },
    trombosprofylax: ({ value }: { value: any }) => {
        return convertTrombosprofylaxData({ trombosprofylax: value?.trombosprofylax_display });
    },
    operationsinformation: ({ value }: { value: any }) => {
        return value?.operationsinformation ?? "";
    },
    studie_kvalitetsregister: ({ value }: { value: any }) => {
        return (
            value?.studie_kvalitetsregister_display?.map((item: StudieKvalitetsregister) => ({
                id: convertStudieKvalitetsregisterData({ studieKvalitetsregister: item }),
            })) ?? []
        );
    },
};

export const typoperationFormTriggers = [
    {
        fieldName: dependentFields,
        dependentOn: "typoperation",
        action: ({
            dependentOnValue,
            initial,
        }: {
            value: { [key: string]: any };
            dependentOnValue: any;
            initial: boolean;
        }) => {
            if (initial) {
                // If the form is being initialized, we return the default values for the dependent fields
                return;
            }

            const store = useStore();

            if (dependentOnValue) {
                const typoperation: {
                    [key: string]: any;
                } = store.getters["operation/typoperation/typoperationById"]({
                    id: dependentOnValue,
                });

                if (typoperation) {
                    return dependentFields.map((fieldName) => {
                        return {
                            fieldName,
                            value: fieldsConverters[fieldName]({
                                value: typoperation,
                            }),
                            suffix: fieldSuffixes[fieldName]({ typoperation }),
                        };
                    });
                }
            }

            return dependentFields.map((fieldName) => {
                return {
                    fieldName,
                    value: fieldName === "operationsinformation" ? "" : null,
                    suffix: null,
                };
            });
        },
    },
] as Array<Trigger>;
